<script module lang="ts">
  export type PriceComponent = {
    valid_from: string;
    valid_on_period?: number;
    valid_to: string;
    value: number;
    complete: boolean;
  };

  export type PriceArea = {
    price_area: "NO1" | "NO2" | "NO3" | "NO4" | "NO5";
    aggregated: PriceComponent;
    periods: PriceComponent[];
  };

  export type PriceData = {
    price_areas: PriceArea[];
  };
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import Map from "./Map.svelte";

  import type { PageProps } from "./$types";

  import { contentBlockComponents } from "$lib/portableText/utils";
  import { PortableText } from "@portabletext/svelte";
  import MetaData from "$lib/MetaData.svelte";
  import Breadcrumb from "$lib/Breadcrumb.svelte";
  import type { InternalLinkingModule } from "$lib/types/shared";
  import InternalLinking from "$lib/InternalLinking.svelte";
  import FAQ from "$lib/FAQ.svelte";
  import Picture from "$lib/SanityPicture.svelte";
  import type { SanityPicture } from "$lib/types/shared";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import { selectAreas } from "$lib/utils/areaUtils";
  import LoadingCircle from "$lib/LoadingCircle.svelte";
  import CustomerServiceBanner from "$lib/CustomerServiceBanner.svelte";

  let { data }: PageProps = $props();

  let norgespris = $derived(data.norgespris_data);
  let bannerImage: SanityPicture = $derived(norgespris.banner_block_image as SanityPicture);
  let internalLinkingModule = $derived(data?.internal_linking_module as InternalLinkingModule);
  let faqs = $derived(norgespris?.faq);

  let selectedArea = $state("");
  let selectedAreaName = $derived.by(() => {
    if (!selectedArea) {
      return "Velg et område i kartet";
    }
    return selectAreas.find((x) => x.value === selectedArea)?.label || "Ukjent";
  });

  const url =
    "https://api.app.ishavskraft.no/api-app/consumption/spot/months?period_from=365d&period_to=today";
  let priceData: PriceData | undefined = $state();

  onMount(async () => {
    const response = await fetch(url);
    const data = (await response.json()) as PriceData;
    priceData = {
      ...data,
      price_areas: data.price_areas.map((area) =>
        area.price_area === "NO4"
          ? area
          : {
              ...area,
              periods: area.periods.map((period) => ({
                ...period,
                value: period.value * 1.25,
              })),
            }
      ),
    };
  });

  let selectedAreaData = $derived(
    priceData?.price_areas.find((area) => area.price_area === selectedArea)?.periods
  );
  let selectedPrice = $derived(
    selectedAreaData
      ? selectedAreaData.reduce((acc, curr) => curr.value + acc, 0) / selectedAreaData.length
      : 0
  );

  const info = () => {
    if (!selectedPrice) {
      return;
    }
    if (selectedPrice > 40) {
      return norgespris.options.find((option) => option.muligheter === "velg");
    } else if (selectedPrice <= 40 && selectedPrice > 30) {
      return norgespris.options.find((option) => option.muligheter === "avvent");
    } else {
      return norgespris.options.find((option) => option.muligheter === "ikke_velg");
    }
  };

  const areaComments = $derived(norgespris?.area_comments);
</script>

<MetaData
  data={{
    title: norgespris?.title,
    description: norgespris?.seoDescription || "",
  }}
/>

<Breadcrumb
  breadcrumbs={[
    { label: "Forside", href: "/" },
    { label: "Norgespris", href: "/norgespris" },
  ]}
/>

<header>
  <h1 class="text-pretty mb-6">{norgespris?.title}</h1>
  <div class="max-w-prose">
    <PortableText
      value={norgespris.description}
      components={contentBlockComponents}
      onMissingComponent={false}
    />
  </div>
</header>

<div class="mx-edge-x max-w-prose">
  <PortableText
    value={norgespris.content}
    components={contentBlockComponents}
    onMissingComponent={false}
  />
</div>

<div class="flex flex-col tablet:flex-row tablet:gap-4 laptop:gap-8 my-8 min-h-[400px] mx-edge-x">
  <div
    aria-labelledby="price-map-heading"
    class="min-w-[400px] w-[400px] min-h-[500px] h-[500px] laptop:w-[500px] laptop:h-[600px] laptop:min-w-[500px] laptop:min-h-[600px]"
  >
    <h2 id="price-map-heading" class="sr-only">Interaktivt strømpris-kart for Norge</h2>
    {#if priceData}
      <Map bind:selectedArea {priceData} width={500} height={600} />
    {:else}
      <div class="flex justify-center items-center h-full">
        <LoadingCircle />
      </div>
    {/if}
  </div>

  <div
    aria-labelledby="price-info-heading {selectedArea}"
    role="tabpanel"
    id="tabpanel-{selectedArea}"
    class="flex-1 text-base bg-$glod p-4 rounded-2xl min-w-[300px] tablet:max-w-96 laptop:max-w-128"
  >
    <h2 id="price-info-heading" class="m-0">{selectedAreaName}</h2>
    {#if info()}
      <p class="mt-0">{selectedArea}</p>
      <p class="mb-0">
        Snittpris siste 12 måneder: <b>{selectedPrice?.toFixed(2).replace(".", ",")} øre/kWh</b>
      </p>
      <p class="mt-0">
        {selectedArea === "NO4" ? "Prisen er eksklusiv moms." : "Prisen er inklusiv moms."}
      </p>
      <div class="text-base">
        <PortableText
          value={info()!.value}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
      {#if areaComments && areaComments.find((comment) => comment.area.toUpperCase() === selectedArea)?.comment?.length}
        <div class="text-base">
          <PortableText
            value={areaComments.find((comment) => comment.area.toUpperCase() === selectedArea)
              ?.comment}
            components={contentBlockComponents}
            onMissingComponent={false}
          />
        </div>
      {/if}
    {/if}
  </div>
</div>

<div class="mx-edge-x max-w-prose">
  <PortableText
    value={norgespris.content_two}
    onMissingComponent={false}
    components={contentBlockComponents}
  />
</div>

{#if norgespris?.banner_block}
  <div class="box mx-edge-x p-4 my-8">
    <div class="grid tablet:grid-cols-[1fr_auto] items-center gap-4 tablet:px-4">
      <div class="laptop:text-lead">
        <PortableText
          value={norgespris.banner_block}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
      {#if bannerImage?.src}
        <div class="tablet:ml-4 laptop:ml-8 display-flex">
          <Picture
            pictureObject={bannerImage}
            sizes={`(min-width: ${br.wide}) 30vw, (min-width: ${br.desktop}) 40vw, (min-width: ${br.tablet}) 45vw, 90vw`}
            alt={bannerImage?.alt || ""}
            class="rounded-xl tablet:max-w-80 laptop:max-w-100 tablet:h-96 tablet:object-cover"
          />
        </div>
      {/if}
    </div>
  </div>
{/if}

{#if faqs && faqs.items?.length > 0}
  <section class="my-12 mx-edge-x">
    <h2>{faqs.heading || `Spørsmål og svar`}</h2>
    <div class="border-solid border-3 rounded-2xl p-2">
      {#each faqs.items as question (question)}
        <FAQ answer={question} />
      {/each}
    </div>
  </section>
{/if}

<CustomerServiceBanner
  showMessageInput={false}
  title="Har du andre spørsmål om Norgespris, gjerne kontakt oss"
/>

{#if internalLinkingModule}
  <InternalLinking module={internalLinkingModule} />
{/if}

<style lang="postcss">
  .box {
    --_box-bg: var(--primary-light);
    background-color: var(--_box-bg);
    border-radius: 1rem;
  }

  .box > :global(*) {
    margin-inline: 0;
  }

  @media (--tablet) {
    .box {
      padding-inline: var(--s-5);
    }
  }
</style>
